import {List as ImmutableList, Map as ImmutableMap} from 'immutable';
import {createSelector} from 'reselect';

/* Constants */
import {FACET_IDS} from '../../views/filter/facet/facetConstants';
import {CATEGORY_PAGE_BRAND_ID_DELIMITER} from '../../modules/category/categoryConstants';

/* Utils */
import {generateFilterApiQueryParams, generateFilterApiQueryString} from '../../modules/filter/filterUtils';
import {createImmutableMapFromList} from '../../common/collection/collectionUtils';

const selectedFiltersByFacetMapSelector = state => state.filterState.selectedFiltersByFacetMap;

const visibleSelectedFiltersByFacetMapSelector = state => state.filterState.visibleSelectedFiltersByFacetMap;

const categoryFacetSelector = state => state.filterState.categoryFacet;

const facetMapSelector = state => state.filterState.facetMap;

const initialFilteredCategorySelector = state => state.filterState.initialFilteredCategory;

const filteredCategorySelector = state => state.filterState.filteredCategory;

const tagIdSelector = state => state.tagState.tagProductPaginationParams.tagId;

const isIgnoreTolkienForSearchFiltersSelector = state => state.filterState.ignoreTolkienForSearchFilters;

const isIgnoreGenderSearchFiltersSelector = state => state.filterState.ignoreGender;

const isIgnoreColorSearchFiltersSelector = state => state.filterState.ignoreColor;

const activeFacet = state => state.filterState.activeFacet;

const isTolkienBrandFiltersAppliedForSearchSelector = state => state.filterState.isTolkienBrandFiltersAppliedForSearchFilters;

const searchContextSelector = state => state.filterState.searchContextForSearchFilters;

const searchTermTypoSelector = state => state.searchState.searchTermTypo;

const searchAutoAppliedFacetCountSelector = state => state.searchState.searchAutoAppliedFacetCount;

const brandIdSelector = state => state.brandState.brandProductPaginationParams.brand;

const appliedFacetArraySelector = state => state.filterState.appliedFacetArray;

const autoAppliedFacetCountSelector = createSelector(
  [searchAutoAppliedFacetCountSelector],
  (autoAppliedCount) => {
    return searchAutoAppliedFacetCountSelector ? autoAppliedCount : 0;
  });

const initialFilteredCategoryLevelSelector = createSelector(
  [initialFilteredCategorySelector],
  (category) => {
    return initialFilteredCategorySelector ? category.level : null;
  });

const brandIdsPathParamSelector = createSelector(
  [selectedFiltersByFacetMapSelector],
  (selectedFiltersByFacetMap) => {
    let result = '';

    if (selectedFiltersByFacetMap) {
      const selectedBrandFilterMap = selectedFiltersByFacetMap.get(FACET_IDS.BRAND);

      if (selectedBrandFilterMap && selectedBrandFilterMap.size > 0) {
        result = Array.from(selectedBrandFilterMap.values()).
          map(brandFilter => brandFilter.id,
          ).
          join(CATEGORY_PAGE_BRAND_ID_DELIMITER);
      }
    }

    return result;
  });

const brandParamSelector = createSelector(
  [facetMapSelector],
  (facetMap) => {

    let result = '';

    if (facetMap) {
      const brandFacet = facetMap.get(FACET_IDS.BRAND);

      if (brandFacet) {
        result = brandFacet?.rawFacet?.items;
      }

    }

    return result

  });

const tagIdsMatchParamSelector = createSelector(
  [tagIdSelector],
  (tagId) => {
    return tagIdSelector ? tagId : '';
  });

const brandIdsMatchParamSelector = createSelector(
  [brandIdSelector],
  (brandId) => {
    return brandIdSelector ? brandId : '';
  });

const isTolkienBrandFiltersAppliedSelector = createSelector(
  [isTolkienBrandFiltersAppliedForSearchSelector],
  (isTolkienBrand) => {
    return isTolkienBrand;
  });

const searchContextForSearchFiltersSelector = createSelector(
  [searchContextSelector],
  (searchContext) => {
    return searchContext;
  });

const searchTermTypoForSearchFiltersSelector = createSelector(
  [searchTermTypoSelector],
  (searchTermTypo) => {
    return searchTermTypo;
  });

const ignoreTolkienForSearchFiltersSelector = createSelector(
  [isIgnoreTolkienForSearchFiltersSelector],
  (isIgnoreTolkienForSearchFilters) => {
    return isIgnoreTolkienForSearchFilters;
  });

const ignoreGenderForSearchFiltersSelector = createSelector(
  [isIgnoreGenderSearchFiltersSelector],
  (ignoreGender) => {
    return ignoreGender;
  });

const ignoreColorForSearchFiltersSelector = createSelector(
  [isIgnoreColorSearchFiltersSelector],
  (ignoreColor) => {
    return ignoreColor;
  });

const isRemoveAutoAppliedFacetForGender = createSelector(
  [
    visibleSelectedFiltersByFacetMapSelector,
    appliedFacetArraySelector,
  ],
  (visibleSelectedFiltersByFacetMap, appliedFacetArraySelector) => {
    let visibleSelectedFilter = visibleSelectedFiltersByFacetMap?.get?.(FACET_IDS.GENDER)?.filter?.(gender => gender.isAutoApplied === true)?.size;
    let hasAppliedFacet = appliedFacetArraySelector?.find?.(filter => filter.id === FACET_IDS.GENDER)?.filterList?.find?.(facet => facet.isAutoApplied);
    return Boolean(!visibleSelectedFilter && hasAppliedFacet)
  }
);

const isRemoveAutoAppliedFacetForColor = createSelector(
  [
    visibleSelectedFiltersByFacetMapSelector,
    appliedFacetArraySelector,
  ],
  (visibleSelectedFiltersByFacetMap, appliedFacetArraySelector) => {
    let visibleSelectedFilter = visibleSelectedFiltersByFacetMap?.get?.(FACET_IDS.COLOR)?.filter?.(color => color.isAutoApplied === true)?.size;
    let hasAppliedFacet = appliedFacetArraySelector?.find?.(filter => filter.id === FACET_IDS.COLOR)?.filterList?.find?.(facet => facet.isAutoApplied);
    return Boolean(!visibleSelectedFilter && hasAppliedFacet)
  }
);

const isRemoveAutoAppliedFacetForBrand = createSelector(
  [
    visibleSelectedFiltersByFacetMapSelector,
    appliedFacetArraySelector,
  ],
  (visibleSelectedFiltersByFacetMap, appliedFacetArraySelector) => {
    let visibleSelectedFilter = visibleSelectedFiltersByFacetMap?.get?.(FACET_IDS.BRAND)?.filter?.(brand => brand.isAutoApplied === true)?.size;
    let hasAppliedFacet = appliedFacetArraySelector?.find?.(filter => filter.id === FACET_IDS.BRAND)?.filterList?.find?.(facet => facet.isAutoApplied);
    return Boolean(!visibleSelectedFilter && hasAppliedFacet)
  },
);

const hasAppliedFiltersSelector = createSelector(
  [
    visibleSelectedFiltersByFacetMapSelector,
    initialFilteredCategorySelector,
    filteredCategorySelector,
  ],
  (
    visibleSelectedFiltersByFacetMap,
    initialFilteredCategory,
    filteredCategory,
  ) => {
    return Boolean(visibleSelectedFiltersByFacetMap) &&
      Array.from(visibleSelectedFiltersByFacetMap.entries()).
        filter(([facetId, selectedFilterMap]) => {
          let result;
          if (facetId === FACET_IDS.CATEGORY) {
            result = filteredCategory.categoryId !== initialFilteredCategory.categoryId;
          } else {
            result = selectedFilterMap.size > 0;
          }

          return result;
        }).length > 0;
  },
);

const hasAppliedFilterOnlyOneAuthorSelector = createSelector(
  [
    visibleSelectedFiltersByFacetMapSelector,
    initialFilteredCategorySelector,
    filteredCategorySelector,
  ],
  (
    visibleSelectedFiltersByFacetMap,
    initialFilteredCategory,
    filteredCategory,
  ) => {
    const appliedFacetsExcludedAuthor = Boolean(visibleSelectedFiltersByFacetMap) &&
      Array.from(visibleSelectedFiltersByFacetMap.entries()).
        filter(([facetId, selectedFilterMap]) => {
          let result;
          if (facetId === FACET_IDS.CATEGORY) {
            result = filteredCategory.categoryId !== initialFilteredCategory.categoryId;
          } else if (facetId !== FACET_IDS.AUTHOR) {
            result = selectedFilterMap.size > 0;
          }

          return result;
        });

    const appliedAuthorFacets = visibleSelectedFiltersByFacetMap?.get(FACET_IDS.AUTHOR);

    return appliedFacetsExcludedAuthor.length === 0 && appliedAuthorFacets?.size === 1;
  },
);

const hasSeoBlockerFiltersAppliedOnApiSelector = createSelector(
  [selectedFiltersByFacetMapSelector],
  (
    selectedFiltersByFacetMap,
  ) => {
    return Boolean(selectedFiltersByFacetMap) &&
      Array.from(selectedFiltersByFacetMap.entries()).
        filter(([facetId, selectedFilterMap]) => {
          let result;

          if (facetId !== FACET_IDS.BRAND) {
            result = selectedFilterMap.size > 0;
          }

          return result;
        }).length > 0;
  },
);

const facetListSelector = createSelector(
  [facetMapSelector],
  (facetMap) => {
    let result;

    if (facetMap) {
      result = ImmutableList(facetMap.values());
    } else {
      result = null;
    }

    return result;
  },
);

const filterApiQueryParamsSelector = createSelector(
  [
    visibleSelectedFiltersByFacetMapSelector,
    searchAutoAppliedFacetCountSelector,
    activeFacet
  ],
  (visibleSelectedFiltersByFacetMap, searchAutoAppliedFacetCount, activeFacet) => {
    let filtersByFacetMapEntries = [];
    if (visibleSelectedFiltersByFacetMap) {
      for (const [facetId, selectedFilterMap] of visibleSelectedFiltersByFacetMap) {
        const filtersByFacetMap = Array.from(selectedFilterMap.values()).
          filter(brand => brand.isAutoApplied === false);
        filtersByFacetMapEntries.push([
          facetId,
          createImmutableMapFromList(filtersByFacetMap),
        ]);
      }
    }

    let visibleSelectedFilterBrand = visibleSelectedFiltersByFacetMap?.get?.(FACET_IDS.BRAND)?.filter?.(brand => brand.isAutoApplied === true)?.size;
    let hasAppliedFacetBrand = appliedFacetArraySelector?.find?.(filter => filter.id === FACET_IDS.BRAND)?.filterList?.find?.(facet => facet.isAutoApplied);
    let isRemoveBrand = Boolean(!visibleSelectedFilterBrand && hasAppliedFacetBrand);

    let visibleSelectedFilterGender = visibleSelectedFiltersByFacetMap?.get?.(FACET_IDS.GENDER)?.filter?.(gender => gender.isAutoApplied === true)?.size;
    let hasAppliedFacetGender = appliedFacetArraySelector?.find?.(filter => filter.id === FACET_IDS.GENDER)?.filterList?.find?.(facet => facet.isAutoApplied);
    let isRemoveGender = Boolean(!visibleSelectedFilterGender && hasAppliedFacetGender)

    let visibleSelectedFilterColor = visibleSelectedFiltersByFacetMap?.get?.(FACET_IDS.COLOR)?.filter?.(color => color.isAutoApplied === true)?.size;
    let hasAppliedFacetColor = appliedFacetArraySelector?.find?.(filter => filter.id === FACET_IDS.COLOR)?.filterList?.find?.(facet => facet.isAutoApplied);
    let isRemoveColor = Boolean(!visibleSelectedFilterColor && hasAppliedFacetColor)

    const filteredVisibleSelectedFiltersByFacetMap = isRemoveBrand || isRemoveGender || isRemoveColor ? visibleSelectedFiltersByFacetMap : ImmutableMap(filtersByFacetMapEntries);
    return generateFilterApiQueryParams(
      filteredVisibleSelectedFiltersByFacetMap,
    );
  },
);

const filterApiQueryParamsStringSelector = createSelector(
  [visibleSelectedFiltersByFacetMapSelector],
  (visibleSelectedFiltersByFacetMap) => {
    return generateFilterApiQueryString(visibleSelectedFiltersByFacetMap);
  },
);

export {
  tagIdsMatchParamSelector,
  tagIdSelector,
  brandIdsMatchParamSelector,
  brandIdSelector,
  facetListSelector,
  brandIdsPathParamSelector,
  filterApiQueryParamsSelector,
  filterApiQueryParamsStringSelector,
  categoryFacetSelector,
  hasAppliedFiltersSelector,
  hasAppliedFilterOnlyOneAuthorSelector,
  hasSeoBlockerFiltersAppliedOnApiSelector,
  visibleSelectedFiltersByFacetMapSelector,
  selectedFiltersByFacetMapSelector,
  ignoreTolkienForSearchFiltersSelector,
  isRemoveAutoAppliedFacetForBrand,
  isRemoveAutoAppliedFacetForGender,
  isRemoveAutoAppliedFacetForColor,
  isTolkienBrandFiltersAppliedSelector,
  searchContextForSearchFiltersSelector,
  searchTermTypoForSearchFiltersSelector,
  ignoreGenderForSearchFiltersSelector,
  ignoreColorForSearchFiltersSelector,
  autoAppliedFacetCountSelector,
  initialFilteredCategoryLevelSelector,
  brandParamSelector,
};
